import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, MapPin } from "lucide-react";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/4109261/4109261-uhd_2560_1440_25fps.mp4";
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Hero({ onBook }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-black text-white"
    >
      <video
        data-testid="hero-video"
        className="absolute inset-0 h-full w-full object-cover opacity-70"
        autoPlay
        muted
        loop
        playsInline
        poster={HERO_FALLBACK}
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/85" />
      <div className="absolute inset-0 grain" />

      <div className="pc-container relative z-10 flex min-h-screen flex-col justify-end pb-20 pt-32 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-[var(--pc-green)]"
        >
          <MapPin size={16} /> Toronto · GTA · Mississauga · Vaughan · Brampton
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display mt-6 max-w-5xl text-5xl font-black leading-[0.92] tracking-tighter sm:text-7xl md:text-8xl"
        >
          Toronto's Most{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[var(--pc-green)]">Reliable</span>
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{ originX: 0 }}
              className="absolute inset-x-0 bottom-1 h-2 bg-[var(--pc-green)]/30"
            />
          </span>{" "}
          Cleaning Service.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 max-w-2xl text-xl text-white/80 sm:text-2xl"
        >
          Homes · Offices · Construction · Commercial. Background-checked teams,
          eco-friendly products, ridiculously crisp results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-button"
            onClick={onBook}
            className="pc-btn-primary text-base"
          >
            Book Free Estimate <ArrowRight size={18} />
          </button>
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollTo("services")}
            className="pc-btn-outline text-base text-white"
          >
            <PlayCircle size={18} /> See What We Clean
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-white/15 pt-8 sm:grid-cols-4"
        >
          {[
            ["500+", "Homes Cleaned"],
            ["120+", "Offices Trusted"],
            ["5★", "Avg. Customer Rating"],
            ["100%", "Satisfaction Promise"],
          ].map(([num, label]) => (
            <div key={label}>
              <div className="font-display text-3xl font-black sm:text-4xl">{num}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
